import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { ExternalEventForm } from '../components/events/ExternalEventForm';
import type { ExternalEventFormValues } from '../components/events/ExternalEventForm';
import { EventCancelModal } from '../components/events/EventCancelModal';
import {
  useGetEventDetail,
  useGetEventDraft,
  useUpdateEventDraft,
  useEditPublishedEvent,
  useCreateEvent,
  useCancelEvent,
  useDeleteEventDraft,
  type ExternalEventCreationPayload,
  type MediaUrlEntry,
  type MediaInstruction,
} from '../api/events';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { resolveDominantColorForSubmit, colorsEqual } from '../utils/dominantColor';
import { FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { toast } from '../utils/toast';
import { Button } from '../components/ui/Button';
import type { LocationBody } from '../services/locationService';
import { DEFAULT_TIMEZONE } from '../components/ui/TimezoneSelect';
import { extractApiError } from '../utils/apiError';
import { wallClockFromUtcIso, wallClockAsPicked } from '../utils/eventDate';
import { useAuth } from '../hooks/useAuth';
import { canEditEvent } from '../utils/eventPermissions';
import { UnauthorizedPage } from '../components/common/UnauthorizedPage';

export const Route = createFileRoute('/events/$eventId/edit-external')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: EditExternalPage,
});

function parseDatetime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  return wallClockFromUtcIso(iso);
}

function formatDateDisplay(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

function EditExternalPage() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);

  const [draftHint, setDraftHint] = useState<boolean | undefined>(undefined);
  const {
    data: event,
    isLoading: isEventLoading,
    error: eventError,
  } = useGetEventDetail(eventId, draftHint);
  const isDraftEvent = event?.is_draft_or_published === 'draft';

  useEffect(() => {
    if (event?.is_draft_or_published) {
      const isDraft = event.is_draft_or_published === 'draft';
      if (isDraft !== draftHint) {
        setDraftHint(isDraft);
      }
    }
  }, [event?.is_draft_or_published, draftHint]);

  const { data: draftEvent, isLoading: isDraftLoading } = useGetEventDraft(eventId, isDraftEvent);
  const updateDraft = useUpdateEventDraft();
  const editPublishedEvent = useEditPublishedEvent();
  const createEvent = useCreateEvent();
  const cancelEvent = useCancelEvent();
  const deleteDraft = useDeleteEventDraft();

  const resolvedEvent = isDraftEvent ? (draftEvent ?? event) : event;
  const isLoading = isEventLoading || (isDraftEvent && isDraftLoading);
  const saveMutation =
    resolvedEvent?.is_draft_or_published === 'draft' ? updateDraft : editPublishedEvent;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </main>
        <BottomNav />
      </div>
    );
  }

  if (eventError || !resolvedEvent) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Event not found</h2>
          <p className="text-zinc-500 mb-6">Unable to load event for editing.</p>
          <Button
            onClick={() => navigate({ to: '/events' })}
            size="lg"
            className="rounded-full px-8"
          >
            Back to Your Events
          </Button>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (!canEditEvent(resolvedEvent, currentUserId)) {
    return <UnauthorizedPage resourceType="event" resourceId={eventId} />;
  }

  const start = parseDatetime(resolvedEvent.start ?? '');
  const end = parseDatetime(resolvedEvent.end ?? '');

  const eventDateDisplay = `${formatDateDisplay(resolvedEvent.start ?? '')}${resolvedEvent.end ? ` — ${formatDateDisplay(resolvedEvent.end)}` : ''}`;
  const eventLocation = [resolvedEvent.city, resolvedEvent.state].filter(Boolean).join(', ');

  const handleCancel = () => {
    if (!eventId) return;
    cancelEvent.mutate(Number(eventId), {
      onSuccess: () => {
        setIsCancelModalOpen(false);
        toast.success('Event cancelled');
        queryClient.invalidateQueries({ queryKey: ['events'] });
        queryClient.invalidateQueries({ queryKey: ['events', 'detail', eventId] });
        navigate({ to: '/events' });
      },
      onError: (err) => {
        toast.error(extractApiError(err, 'Failed to cancel event'));
      },
    });
  };

  const handleDeleteDraft = () => {
    if (!eventId) return;
    deleteDraft.mutate(
      { draftId: Number(eventId), draftType: 'single-draft' },
      {
        onSuccess: () => {
          setIsDeleteDraftModalOpen(false);
          toast.success('Draft deleted');
          navigate({ to: '/events' });
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to delete draft'));
        },
      },
    );
  };

  const handleSubmit = async (values: ExternalEventFormValues, action: 'save' | 'publish') => {
    const newStart = wallClockAsPicked(values.startDate, values.startTime);
    const newEnd = wallClockAsPicked(values.endDate, values.endTime);

    const dominantColor = await resolveDominantColorForSubmit(
      values.images[0],
      values.dominantColor,
    );

    let newMediaUrls: MediaUrlEntry[] = [];
    const newFiles = values.imageFiles.filter((f): f is File => f !== null);
    if (newFiles.length > 0) {
      try {
        const results = await uploadFilesToCloudinary(newFiles, {
          folder: 'event_images',
          uploadPreset: 'event_preset',
        });
        newMediaUrls = results.map((r) => ({
          public_id: r.public_id,
          secure_url: r.secure_url,
          resource_type: r.resource_type,
          bytes: r.bytes,
          format: r.format,
        }));
      } catch {
        toast.error('Failed to upload images. Please try again.');
        return;
      }
    }

    const mediaInstructions: MediaInstruction[] = (values.deletedImageIds ?? []).map((id) => ({
      id,
      delete: true,
    }));

    const isPublished = resolvedEvent?.is_draft_or_published === 'published';
    if (action === 'save') {
      const eventDiff: Record<string, unknown> = {};

      if (values.event_title !== initialData.event_title) {
        eventDiff.event_title = values.event_title;
      }
      if (values.info !== initialData.info) {
        eventDiff.info = values.info;
      }

      const initialLoc = initialData.locationBody;
      const locChanged =
        !initialLoc ||
        values.locationBody?.address !== initialLoc.address ||
        values.locationBody?.city !== initialLoc.city ||
        values.locationBody?.state !== initialLoc.state ||
        values.locationBody?.country !== initialLoc.country ||
        values.locationBody?.latitude !== initialLoc.latitude ||
        values.locationBody?.longitude !== initialLoc.longitude;

      if (locChanged) {
        eventDiff.location_body = values.locationBody
          ? {
              street: values.locationBody.address,
              city: values.locationBody.city,
              state: values.locationBody.state,
              country: values.locationBody.country,
              longitude: values.locationBody.longitude,
              latitude: values.locationBody.latitude,
            }
          : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 };
      }

      const currentAddress = values.locationDetails || values.locationBody?.address || '';
      const initialAddress = initialData.locationDetails || initialData.locationBody?.address || '';
      if (currentAddress !== initialAddress) {
        eventDiff.address = currentAddress;
      }

      if (values.timezone !== initialData.timezone) {
        eventDiff.start = newStart;
        eventDiff.end = newEnd;
        eventDiff.iana_timezone = values.timezone;
      }
      if (
        values.startDate !== initialData.startDate ||
        values.startTime !== initialData.startTime
      ) {
        eventDiff.start = newStart;
      }
      if (values.endDate !== initialData.endDate || values.endTime !== initialData.endTime) {
        eventDiff.end = newEnd;
      }
      if (eventDiff.start !== undefined || eventDiff.end !== undefined) {
        eventDiff.iana_timezone = values.timezone;
      }

      if (values.external_ticketing_url !== initialData.external_ticketing_url) {
        eventDiff.external_ticketing_url = values.external_ticketing_url ?? '';
      }

      if (!colorsEqual(dominantColor, initialData.dominantColor)) {
        eventDiff.dominant_color = dominantColor;
      }

      const initialCollabIds = initialData.collaboratorIds ?? [];
      const collaboratorsChanged =
        JSON.stringify([...(values.collaboratorIds ?? [])].sort()) !==
        JSON.stringify([...initialCollabIds].sort());

      if (collaboratorsChanged) {
        eventDiff.collaborators = values.collaboratorIds;
      }

      if (isPublished) {
        editPublishedEvent.mutate(
          {
            eventId: Number(eventId),
            payload: {
              event: eventDiff,
              ...(newMediaUrls.length > 0 ? { media_urls: newMediaUrls } : {}),
              ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
              ...(newFiles.length > 0 ? { images: newFiles } : {}),
            },
          },
          {
            onSuccess: () => {
              toast.success('Event updated successfully!');
              queryClient.removeQueries({ queryKey: ['events', 'detail', eventId] });
              navigate({ to: '/events/$eventId', params: { eventId } });
            },
            onError: (err) => {
              toast.error(extractApiError(err, 'Failed to update event'));
            },
          },
        );
        return;
      } else {
        const draftUpdatePayload = {
          event: eventDiff,
          ...(newMediaUrls.length > 0 ? { media_urls: newMediaUrls } : {}),
          ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
        };

        updateDraft.mutate(
          { eventId: Number(eventId), payload: draftUpdatePayload },
          {
            onSuccess: () => {
              toast.success('Draft saved!');
              queryClient.removeQueries({ queryKey: ['events', 'detail', eventId] });
              queryClient.removeQueries({ queryKey: ['events', 'draft', eventId] });
              navigate({ to: '/events/$eventId', params: { eventId } });
            },
            onError: (err) => {
              toast.error(extractApiError(err, 'Failed to update event'));
            },
          },
        );
        return;
      }
    }

    const eventPayload: ExternalEventCreationPayload = {
      event_title: values.event_title,
      event_type: resolvedEvent?.event_type ?? 'physical',
      draft_id: resolvedEvent?.is_draft_or_published === 'draft' ? Number(eventId) : undefined,
      location_body: values.locationBody
        ? {
            street: values.locationBody.address,
            city: values.locationBody.city,
            state: values.locationBody.state,
            country: values.locationBody.country,
            longitude: values.locationBody.longitude,
            latitude: values.locationBody.latitude,
          }
        : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 },
      start: newStart,
      end: newEnd,
      address: values.locationDetails || values.locationBody?.address || '',
      collaborators: values.collaboratorIds.length ? values.collaboratorIds : undefined,
      external_ticketing_url: values.external_ticketing_url ?? '',
      event_ticketing_type: 'external',
      iana_timezone: values.timezone,
      info: values.info,
      dominant_color: dominantColor,
    };

    const existingMediaUrls: MediaUrlEntry[] = values.existingImageIds
      .map((id, i) => ({ id, url: values.images[i] }))
      .filter((x) => x.id != null && x.url)
      .map((x) => ({ public_id: '', secure_url: x.url, resource_type: 'image' }));
    const allMediaUrls = [...existingMediaUrls, ...newMediaUrls];

    const fullPayload = {
      event: eventPayload,
      ...(allMediaUrls.length > 0 ? { media_urls: allMediaUrls } : {}),
      ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
    };

    if (action === 'publish') {
      createEvent.mutate(fullPayload, {
        onSuccess: (data) => {
          toast.success('External event published!');
          const publishedId = data.event_id ?? data.event?.id;
          queryClient.removeQueries({ queryKey: ['events', 'detail', eventId] });
          queryClient.removeQueries({ queryKey: ['events', 'draft', eventId] });
          navigate({ to: '/events/$eventId', params: { eventId: String(publishedId ?? eventId) } });
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to publish external event'));
        },
      });
      return;
    }
  };

  const initialData: Partial<ExternalEventFormValues> = {
    event_title: resolvedEvent.event_title ?? '',
    info: resolvedEvent.info ?? '',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    timezone: resolvedEvent.timezone ?? DEFAULT_TIMEZONE,
    locationBody: {
      address: resolvedEvent.venue ?? resolvedEvent.address ?? '',
      city: resolvedEvent.city ?? '',
      state: resolvedEvent.state ?? '',
      country: resolvedEvent.country ?? '',
      latitude: 0,
      longitude: 0,
    } as LocationBody,
    locationDetails: '',
    images: (resolvedEvent.medias ?? []).map((m) => m.file_url ?? '').filter(Boolean),
    imageFiles: (resolvedEvent.medias ?? []).map(() => null),
    existingImageIds: (resolvedEvent.medias ?? []).map((m) => m.id),
    dominantColor: resolvedEvent.dominant_color ?? null,
    external_ticketing_url: resolvedEvent.external_ticketing_url ?? '',
    collaboratorIds: (resolvedEvent.collaborators ?? []).map((c) => c.id),
    is_draft_or_published:
      (resolvedEvent.is_draft_or_published as 'draft' | 'published') ?? 'draft',
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main className="pt-24 md:pt-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>
        <ExternalEventForm
          mode="edit"
          title="Edit External Event"
          statusBadge={resolvedEvent.is_draft_or_published as 'draft' | 'published'}
          {...(resolvedEvent?.is_draft_or_published !== 'draft'
            ? {
                secondaryActionLabel: 'Cancel Event',
                onSecondaryAction: () => setIsCancelModalOpen(true),
              }
            : {
                secondaryActionLabel: 'Delete Draft',
                onSecondaryAction: () => setIsDeleteDraftModalOpen(true),
              })}
          onSubmit={handleSubmit}
          initialData={initialData}
          isLoading={saveMutation.isPending}
        />
      </main>
      <BottomNav />
      {isCancelModalOpen && (
        <EventCancelModal
          eventTitle={resolvedEvent.event_title ?? ''}
          eventDate={eventDateDisplay}
          eventLocation={eventLocation}
          ticketsSold={0}
          eventImage={resolvedEvent.medias?.[0]?.file_url}
          onConfirm={handleCancel}
          onCancel={() => setIsCancelModalOpen(false)}
          isLoading={cancelEvent.isPending}
        />
      )}
      {isDeleteDraftModalOpen && (
        <EventCancelModal
          variant="delete"
          eventTitle={resolvedEvent.event_title ?? ''}
          eventDate={eventDateDisplay}
          eventLocation={eventLocation}
          ticketsSold={0}
          eventImage={resolvedEvent.medias?.[0]?.file_url}
          onConfirm={handleDeleteDraft}
          onCancel={() => setIsDeleteDraftModalOpen(false)}
          isLoading={cancelEvent.isPending}
        />
      )}
    </div>
  );
}
